import type { ReactNode } from 'react';
import { SegmentedTabs, type SegmentedTabItem } from '@/ui';
import type { UseServerListResult } from '@/lib/task-server-list';
import { activeFilterCount, type ListFilters } from '@/lib/task-list-url';
import { ListEmpty, ListError, ListSkeleton, Pager } from './ListStates';
import { ListToolbar, type SortOption } from './ListToolbar';
import styles from './ListScreen.module.css';

/**
 * DORT LISTE EKRANININ ORTAK ISKELETI.
 *
 * Baslik + sekmeler + arac cubugu + durumlar + sayfa gezintisi. Ekranlar yalniz
 * KENDI parametrelerini ve SATIRINI veriyor.
 *
 * Ortak olmasinin sebebi gorunum degil DAVRANIS: yukleniyor/bos/hata/sonuc-yok
 * ayrimi ve "filtre aciksa cikis yolu goster" kurali dort ekranda da AYNI
 * olmali. Kopyalandiginda birinde duzeltilen bir davranis otekilerde yasamaya
 * devam eder — mobil tarafta tam olarak bu oldu.
 */
export type ListScreenProps<TItem> = {
  title: string;
  /** Tek sekme varsa cubuk HIC cizilmez; secenegi olmayan bir secici gurultudur. */
  tabs?: readonly SegmentedTabItem[];
  tab: string;
  onTabChange: (tab: string) => void;
  /** Arac cubugu istenmiyorsa (tek goreve filtreli listeler) verilmez. */
  filters?: ListFilters;
  onFiltersChange?: (filters: ListFilters) => void;
  sortOptions?: readonly SortOption[];
  /** Arac cubugu yerine cizilecek daha basit bir denetim (orn. siralama cipleri). */
  toolbar?: ReactNode;
  list: UseServerListResult<TItem>;
  onOffsetChange: (offset: number) => void;
  onClearFilters: () => void;
  renderRow: (item: TItem) => ReactNode;
  /** Kararli satir anahtari. Dizi indeksi KULLANILMAZ: sayfa degistiginde
      React yanlis satiri yeniden kullanir ve acik banka panelleri yer degistirir. */
  rowKey: (item: TItem) => string;
  /** Listenin ustunde duracak ek icerik (gorev ozeti gibi). */
  header?: ReactNode;
  /**
   * Bos listenin ACIKLAMASI (CEVRILMIS). Bazi listelerin bos olmasi bir KURALIN ya da bir
   * SURECIN sonucu ve kullanici bunu bilmeli — aciklamasiz "Henuz kayit yok" yanlis okunur.
   * (2026-09-23 - 2026-10-02 arasi musterinin teklif listeleri teklif fazinda BILEREK bostu;
   * o kural kalkti, teklifler artik canli.)
   */
  emptyDescription?: string | undefined;
};

export function ListScreen<TItem>({
  title,
  tabs,
  tab,
  onTabChange,
  filters,
  onFiltersChange,
  sortOptions,
  toolbar,
  list,
  onOffsetChange,
  onClearFilters,
  renderRow,
  rowKey,
  header,
  emptyDescription,
}: ListScreenProps<TItem>) {
  const filterCount = filters ? activeFilterCount(filters) : 0;

  return (
    <section className={styles.screen}>
      <h1 className={styles.title}>{title}</h1>

      {header}

      {tabs && tabs.length > 1 ? (
        <SegmentedTabs items={tabs} value={tab} onChange={onTabChange} label={title} block />
      ) : null}

      {filters && onFiltersChange && sortOptions ? (
        <ListToolbar filters={filters} onChange={onFiltersChange} sortOptions={sortOptions} />
      ) : (
        toolbar
      )}

      {list.isLoading ? (
        <ListSkeleton />
      ) : list.isError ? (
        <ListError error={list.error} onRetry={list.refetch} />
      ) : list.items.length === 0 ? (
        // Filtre aciksa "henuz kayit yok" YANILTICI olur: bu kullanicinin kendi
        // filtresinin sonucu ve duzeltilebilir bir durum.
        <ListEmpty
          onClearFilters={filterCount > 0 ? onClearFilters : undefined}
          description={emptyDescription}
        />
      ) : (
        <ul className={[styles.list, list.isFetching ? styles.busy : undefined].filter(Boolean).join(' ')}>
          {list.items.map((item) => (
            <li key={rowKey(item)}>{renderRow(item)}</li>
          ))}
        </ul>
      )}

      <Pager
        offset={list.offset}
        pageSize={list.pageSize}
        returned={list.items.length}
        hasMore={list.hasMore}
        busy={list.isFetching}
        onOffsetChange={onOffsetChange}
      />
    </section>
  );
}
